'use client'

import { usePathname, useRouter } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { useEffect, useMemo, useState } from 'react'
import { useCart } from '@/lib/commerce/hooks'
import { isProfileRoute } from '@/lib/commerce/hooks'
import { useScrollVisibility } from '@/hooks/use-scroll-visibility'
import { formatImageURL, isCustomerAuthActive } from '@/utils/util'
import { Blocks, Home, ListTodo, Package, ShoppingCart, User } from 'lucide-react'
import { useSession } from 'next-auth/react'
import Avatar from 'boring-avatars'
import { loadTemplate } from '@/lib/template-loader'

export default function MobileBottomNav({ vendor, customer, subscription, setMobileMenuOpen }: { vendor: any, customer: any, subscription: any, setMobileMenuOpen: (open: boolean) => void }) {
  const { data: session } = useSession()
  const pathname = usePathname()
  const router = useRouter()
  const { cartCount } = useCart()
  const isVisible = useScrollVisibility()
  const [isProfileLoading, setIsProfileLoading] = useState(true)
  const [openProfile, setOpenProfile] = useState(false)

  const ProfileDrawer = useMemo(() => loadTemplate(vendor, 'profile/ProfileDrawer'), [vendor?.template?.slug, vendor?.template?.isActive])

  useEffect(() => {
    if (customer) {
      setIsProfileLoading(false)
    }
  }, [customer])

  const isProfileActive = () => isProfileRoute(pathname)
  const isLoggedIn = !!session?.user?.id

  const navItems = [
    {
      icon: Home,
      label: 'Нүүр',
      path: '/',
      isActive: pathname === '/' || (!pathname.includes('/product') && !pathname.includes('/cart') && !isProfileActive()),
      onClick: () => router.push(navigateUrl('/', pathname))
    },
    {
      icon: Package,
      label: 'Бараа',
      path: '/product',
      isActive: pathname.includes('/product') && !pathname.includes('/product/'),
      onClick: () => router.push(navigateUrl('/product', pathname))
    },
    ...(vendor?.canPlaceOrder !== false ? [{
      icon: ShoppingCart,
      label: 'Сагс',
      path: '/cart',
      isActive: pathname.includes('/cart'),
      badge: cartCount > 0 ? cartCount : null,
      onClick: () => router.push(navigateUrl('/cart', pathname))
    }] : []),
    isCustomerAuthActive(vendor) ? {
      icon: User,
      label: isLoggedIn ? 'Профайл' : 'Нэвтрэх',
      path: isLoggedIn ? '/profile' : '/login',
      isActive: isProfileActive(),
      isProfile: true,
      onClick: () => {
        if (isLoggedIn) {
          setOpenProfile(true)
        } else {
          router.push(navigateUrl('/login', pathname))
        }
      }
    } : {
      icon: ListTodo,
      label: 'Захиалга',
      path: '/order/check',
      isActive: pathname.includes('/order/check'),
      isProfile: false,
      onClick: () => router.push(navigateUrl('/order/check', pathname))
    },
    {
      icon: Blocks,
      label: 'Бусад',
      path: '/menu',
      isActive: false,
      onClick: () => setMobileMenuOpen(true)
    },
  ]

  return (
    <>
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl transition-transform duration-300 ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ background: 'rgba(8,8,10,0.92)', borderTop: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="grid items-center justify-around py-1" style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}>
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.path}
                onClick={item.onClick}
                className="flex flex-1 flex-col items-center justify-center p-2 min-w-0 relative transition-colors"
                style={{ color: item.isActive ? 'white' : 'rgba(255,255,255,0.55)' }}
              >
                <div className="relative">
                  {item.isProfile && isLoggedIn ? (
                    <div>
                      {session.user?.avatar ? (
                        <img src={formatImageURL(session.user.avatar)} alt="profile" className="size-5.5 rounded-full object-cover" />
                      ) : (
                        <Avatar size={22} variant="marble" name={session.user?.email || undefined} />
                      )}
                    </div>
                  ) : (
                    <Icon className="size-5.5" />
                  )}
                  {item.badge && (
                    <div
                      className="absolute -top-2 -right-4 text-[11px] rounded-full h-5 w-5 flex items-center justify-center font-semibold tabular-nums"
                      style={{ background: 'white', color: '#0a0a0c' }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </div>
                  )}
                </div>
                <span className="text-[13px] font-medium mt-2 leading-none">{item.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <ProfileDrawer open={openProfile} onClose={() => setOpenProfile(false)} vendor={vendor} customer={customer} isLoading={isProfileLoading} setIsLoading={setIsProfileLoading} />
    </>
  )
}
